import { WeatherStationObservationDataKey } from 'features/weatherStations/weatherStations.types';
import { ReactNode } from 'react';
import NavigationIcon from '@mui/icons-material/Navigation';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { InfoTooltip } from 'components/InfoTooltip';
import { useTranslation } from 'react-i18next';
import { convertCelsiusToFahrenheit } from 'features/weatherStations/utils/convertCelsiusToFahrenheit';
import { parseNumericValue } from 'features/weatherStations/utils/parseNumericValue';
import { convertMetersPerSecToMph } from 'features/weatherStations/utils/convertMetersPerSecToMph';
import { EMPTY_DATA_VALUE } from '../../constants';

export const getObservationSensorTypeGroups = (
  isPrecipitationEnabled: boolean,
): WeatherStationObservationDataKey[][] => [
  isPrecipitationEnabled ? ['airTemp', 'precipitation1Hr'] : ['airTemp'],
  ['windDirectionToward', 'windSpeed', 'windGust'],
  ['dewPointTemperature', 'relativeHumidity'],
];

const PrecipitationLabel = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <InfoTooltip title={t('weatherStations.precipitationTooltip')}>
      <WaterDropIcon sx={{ fontSize: 14 }} />
    </InfoTooltip>
  );
};

export const OBSERVATION_SENSOR_TYPE_TO_LABEL: Record<
  WeatherStationObservationDataKey,
  ReactNode
> = {
  airTemp: '°F',
  dewPointTemperature: 'DEW',
  precipitation1Hr: <PrecipitationLabel />,
  relativeHumidity: (
    <>
      REL
      <br />
      HUM
    </>
  ),
  windDirectionToward: (
    <NavigationIcon sx={{ fontSize: 14, rotate: '45deg' }} />
  ),
  windSpeed: 'MPH',
  windGust: 'GUST',
};

export const OBSERVATIONS_DATA_PARSERS: Record<
  string,
  (value?: number) => ReactNode
> = {
  airTemp: (val?: number) =>
    val != null ? `${convertCelsiusToFahrenheit(val, 0)}˚` : EMPTY_DATA_VALUE,
  dewPointTemperature: (val?: number) =>
    val != null ? `${convertCelsiusToFahrenheit(val, 0)}˚` : EMPTY_DATA_VALUE,
  precipitation1Hr: (val?: number) =>
    val != null ? `${parseNumericValue(val, 1)}` : EMPTY_DATA_VALUE,
  relativeHumidity: (val?: number) =>
    val != null ? `${parseNumericValue(val, 0)}` : EMPTY_DATA_VALUE,
  windDirectionToward: (val?: number) =>
    val != null ? (
      <NavigationIcon
        sx={{
          fontSize: 12,
          rotate: `${parseNumericValue(val, 0)}deg`,
        }}
      />
    ) : (
      EMPTY_DATA_VALUE
    ),
  windSpeed: (val?: number) =>
    val != null ? convertMetersPerSecToMph(val, 0) : EMPTY_DATA_VALUE,
  windGust: (val?: number) =>
    val != null ? convertMetersPerSecToMph(val, 0) : EMPTY_DATA_VALUE,
};
